import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
    Button,
    DatePicker,
    Empty,
    Input,
    Modal,
    Pagination,
    Popconfirm,
    Select,
    Spin,
    Tag,
    Typography,
    message,
} from "antd";
import { DeleteOutlined, DownloadOutlined, EyeOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import {
    deleteFeedback,
    FEEDBACK_DEFAULT_PAGE_SIZE,
    FEEDBACK_MAX_PAGE_SIZE,
    listFeedbacks,
} from "../../utils/feedbackAdminApi";
import { useTranslation } from "../../i18n";
import "../delivery/delivery.css";
import "../selection/selection.css";

const { Paragraph, Text } = Typography;

function escapeCsvCell(value) {
    const str = value == null ? "" : String(value);
    if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function downloadCsv(filename, content) {
    const blob = new Blob(["\uFEFF", content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function truncateText(value, max = 80) {
    const str = value == null ? "" : String(value);
    if (str.length <= max) {
        return str;
    }
    return `${str.slice(0, max)}…`;
}

function FeedbackResults() {
    const { t } = useTranslation();
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewing, setViewing] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(FEEDBACK_DEFAULT_PAGE_SIZE);
    const [hasMore, setHasMore] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [appliedQuery, setAppliedQuery] = useState({ q: "", from: null, to: null, sourceType: "" });

    const loadFeedbacks = useCallback(async () => {
        setLoading(true);
        try {
            const result = await listFeedbacks({
                q: appliedQuery.q || undefined,
                from: appliedQuery.from ?? undefined,
                to: appliedQuery.to ?? undefined,
                sourceType: appliedQuery.sourceType || undefined,
                page,
                pageSize,
            });
            setFeedbacks(result.feedbacks || []);
            setHasMore(Boolean(result.hasMore));
        } catch {
            message.error(t("feedback.admin.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [appliedQuery, page, pageSize, t]);

    useEffect(() => {
        loadFeedbacks();
    }, [loadFeedbacks]);

    const handleSearch = useCallback(() => {
        const from = dateRange?.[0] ? dateRange[0].startOf("day").unix() : null;
        const to = dateRange?.[1] ? dateRange[1].endOf("day").unix() : null;
        setPage(1);
        setAppliedQuery({
            q: searchText.trim(),
            from,
            to,
            sourceType: sourceFilter,
        });
    }, [dateRange, searchText, sourceFilter]);

    const handleResetFilters = useCallback(() => {
        setSearchText("");
        setSourceFilter("");
        setDateRange(null);
        setPage(1);
        setAppliedQuery({ q: "", from: null, to: null, sourceType: "" });
    }, []);

    const handleDelete = useCallback(
        async (id) => {
            try {
                await deleteFeedback(id);
                message.success(t("feedback.admin.deleted"));
                loadFeedbacks();
            } catch {
                message.error(t("feedback.admin.deleteFailed"));
            }
        },
        [loadFeedbacks, t]
    );

    const handleExportCsv = useCallback(() => {
        try {
            const headerRow = [
                t("feedback.admin.colTime"),
                t("feedback.admin.colSource"),
                t("feedback.admin.colAlbum"),
                t("feedback.admin.colLinkId"),
                t("feedback.admin.colContent"),
                t("feedback.admin.colContact"),
                t("feedback.admin.colIp"),
            ];
            const dataRows = feedbacks.map((item) => [
                dayjs((item.createdAt || 0) * 1000).format("YYYY-MM-DD HH:mm"),
                item.sourceType === "delivery"
                    ? t("feedback.admin.sourceDelivery")
                    : t("feedback.admin.sourceSelection"),
                item.albumName || item.albumPath || "",
                item.linkId || "",
                item.content || "",
                item.contact || "",
                item.ip || "",
            ]);
            const csv = [headerRow, ...dataRows]
                .map((row) => row.map(escapeCsvCell).join(","))
                .join("\r\n");
            downloadCsv(`feedback-${dayjs().format("YYYYMMDD-HHmm")}.csv`, csv);
            message.success(t("feedback.admin.exportDone"));
        } catch {
            message.error(t("feedback.admin.exportFailed"));
        }
    }, [feedbacks, t]);

    const sourceLabel = useCallback(
        (sourceType) =>
            sourceType === "delivery"
                ? t("feedback.admin.sourceDelivery")
                : t("feedback.admin.sourceSelection"),
        [t]
    );

    const paginationTotal = hasMore
        ? page * pageSize + 1
        : (page - 1) * pageSize + feedbacks.length;

    const hasActiveFilters = Boolean(
        appliedQuery.q || appliedQuery.from || appliedQuery.to || appliedQuery.sourceType
    );

    const sourceOptions = useMemo(
        () => [
            { value: "", label: t("feedback.admin.allSources") },
            { value: "delivery", label: t("feedback.admin.sourceDelivery") },
            { value: "selection", label: t("feedback.admin.sourceSelection") },
        ],
        [t]
    );

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="delivery-page">
                <div className="custom-h1">
                    <span>{t("feedback.admin.title")}</span>
                </div>
                <Paragraph
                    style={{
                        textAlign: "center",
                        color: "var(--color-text-subtle)",
                        fontSize: "0.9rem",
                        maxWidth: 560,
                        margin: "0 auto 1.5rem",
                    }}
                >
                    {t("feedback.admin.subtitle")}
                </Paragraph>

                <div className="delivery-admin-section">
                    <div className="selection-results__toolbar">
                        <div className="selection-results__filters">
                            <Input
                                allowClear
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                onPressEnter={handleSearch}
                                placeholder={t("feedback.admin.searchPlaceholder")}
                                className="selection-results__search"
                            />
                            <Select
                                value={sourceFilter}
                                onChange={setSourceFilter}
                                options={sourceOptions}
                                className="selection-results__source-filter"
                                style={{ minWidth: 120 }}
                            />
                            <DatePicker.RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                allowEmpty={[true, true]}
                                className="selection-results__date-range"
                                placeholder={[t("feedback.admin.dateFrom"), t("feedback.admin.dateTo")]}
                            />
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                                {t("feedback.admin.search")}
                            </Button>
                            {hasActiveFilters ? (
                                <Button onClick={handleResetFilters}>
                                    {t("feedback.admin.resetFilters")}
                                </Button>
                            ) : null}
                        </div>
                        <div className="selection-results__toolbar-actions">
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleExportCsv}
                                disabled={feedbacks.length === 0}
                            >
                                {t("feedback.admin.exportCsv")}
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={loadFeedbacks}>
                                {t("feedback.admin.refresh")}
                            </Button>
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                            <Spin />
                        </div>
                    ) : feedbacks.length === 0 ? (
                        <Empty
                            description={
                                hasActiveFilters
                                    ? t("feedback.admin.emptyFiltered")
                                    : t("feedback.admin.empty")
                            }
                        />
                    ) : (
                        <>
                            <div className="delivery-admin-list">
                                {feedbacks.map((item) => (
                                    <div key={item.id} className="delivery-admin-list__item">
                                        <div className="delivery-admin-list__main">
                                            <div className="delivery-admin-list__title">
                                                <Text strong>
                                                    {item.albumName ||
                                                        item.albumPath ||
                                                        t("feedback.admin.unknownAlbum")}
                                                </Text>
                                                <Tag color={item.sourceType === "delivery" ? "blue" : "green"}>
                                                    {sourceLabel(item.sourceType)}
                                                </Tag>
                                            </div>
                                            <Text type="secondary" className="delivery-admin-list__meta">
                                                {t("feedback.admin.submittedAt", {
                                                    time: dayjs((item.createdAt || 0) * 1000).format(
                                                        "YYYY-MM-DD HH:mm"
                                                    ),
                                                })}
                                                {item.linkId ? ` · ${item.linkId}` : ""}
                                                {item.ip ? ` · ${item.ip}` : ""}
                                            </Text>
                                            <Text className="delivery-admin-list__summary">
                                                {truncateText(item.content)}
                                            </Text>
                                            {item.contact ? (
                                                <Text type="secondary" className="delivery-admin-list__meta">
                                                    {item.contact}
                                                </Text>
                                            ) : null}
                                        </div>
                                        <div className="delivery-admin-list__actions">
                                            <Button
                                                size="small"
                                                icon={<EyeOutlined />}
                                                onClick={() => setViewing(item)}
                                            >
                                                {t("feedback.admin.view")}
                                            </Button>
                                            <Popconfirm
                                                title={t("feedback.admin.deleteConfirm")}
                                                okText={t("feedback.admin.deleteOk")}
                                                cancelText={t("feedback.admin.deleteCancel")}
                                                onConfirm={() => handleDelete(item.id)}
                                            >
                                                <Button size="small" danger icon={<DeleteOutlined />} />
                                            </Popconfirm>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="selection-results__pagination">
                                <Pagination
                                    current={page}
                                    pageSize={pageSize}
                                    total={paginationTotal}
                                    showSizeChanger
                                    pageSizeOptions={[10, 20, FEEDBACK_MAX_PAGE_SIZE]}
                                    onChange={(nextPage, nextPageSize) => {
                                        setPage(nextPage);
                                        setPageSize(nextPageSize);
                                    }}
                                    showTotal={(total, range) => {
                                        const base = t("feedback.admin.pageRange", {
                                            from: range[0],
                                            to: range[1],
                                        });
                                        return hasMore ? `${base}${t("feedback.admin.hasMoreSuffix")}` : base;
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Modal
                open={Boolean(viewing)}
                title={t("feedback.admin.detailTitle")}
                onCancel={() => setViewing(null)}
                footer={[
                    <Button key="close" onClick={() => setViewing(null)}>
                        {t("feedback.modal.cancel")}
                    </Button>,
                ]}
                width={640}
            >
                {viewing ? (
                    <div className="feedback-detail">
                        <p>
                            <Text type="secondary">{t("feedback.admin.colTime")}: </Text>
                            {dayjs((viewing.createdAt || 0) * 1000).format("YYYY-MM-DD HH:mm")}
                        </p>
                        <p>
                            <Text type="secondary">{t("feedback.admin.colSource")}: </Text>
                            {sourceLabel(viewing.sourceType)}
                        </p>
                        <p>
                            <Text type="secondary">{t("feedback.admin.colAlbum")}: </Text>
                            {viewing.albumName || viewing.albumPath || t("feedback.admin.unknownAlbum")}
                        </p>
                        {viewing.albumPath ? (
                            <p>
                                <Text type="secondary">{t("feedback.admin.albumPath")}: </Text>
                                {viewing.albumPath}
                            </p>
                        ) : null}
                        <p>
                            <Text type="secondary">{t("feedback.admin.colLinkId")}: </Text>
                            {viewing.linkId}
                        </p>
                        {viewing.ip ? (
                            <p>
                                <Text type="secondary">{t("feedback.admin.colIp")}: </Text>
                                {viewing.ip}
                            </p>
                        ) : null}
                        {viewing.contact ? (
                            <p>
                                <Text type="secondary">{t("feedback.admin.colContact")}: </Text>
                                {viewing.contact}
                            </p>
                        ) : null}
                        <Paragraph style={{ whiteSpace: "pre-wrap", marginTop: 12 }}>
                            {viewing.content}
                        </Paragraph>
                    </div>
                ) : null}
            </Modal>

            <Footer />
        </>
    );
}

export default FeedbackResults;
